// iter108 — Performance Archive
// Searchable repository of auto-generated CSRs + uploaded POs from completed
// leads. TE uses this to pull evidence for tender performance-criteria
// clauses (past supplies, customer references, satisfaction certificates).
//
// Route: /t/<slug>/performance-archive
// Backend: GET /api/performance-archive (search) + POST /zip + POST /backfill

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import useAuthStore from '../store/authStore';
import { Archive, MagnifyingGlass, FileArrowDown, ArrowClockwise, CheckSquare, Square, Sparkle, ClipboardText, X } from '@phosphor-icons/react';

import { confirm } from '../components/ConfirmDialog';
import { notify } from '../lib/notify';
import { TableRowsSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authedUrl = (url) => {
  if (!url) return '';
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const base = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  return token ? `${base}${base.includes('?') ? '&' : '?'}auth=${encodeURIComponent(token)}` : base;
};

const inr = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${v.toLocaleString('en-IN')}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function PerformanceArchive() {
  const { user } = useAuthStore();
  const canBackfill = user?.role === 'President';

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [matched, setMatched] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [q, setQ] = useState('');
  const [product, setProduct] = useState('');
  const [region, setRegion] = useState('');
  const [state, setState] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');

  // Selection for ZIP
  const [selected, setSelected] = useState(new Set());
  const [zipping, setZipping] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  // iter109 — Suggest-for-tender dialog state
  const [suggestOpen, setSuggestOpen] = useState(false);

  const fetchRows = async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (q) params.q = q;
      if (product) params.product = product;
      if (region) params.region = region;
      if (state) params.state = state;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (minValue) params.min_value = minValue;
      if (maxValue) params.max_value = maxValue;
      const res = await axios.get(`${API}/performance-archive`, { params });
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setMatched(res.data.matched || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  // Debounced search — re-fetch when any filter changes.
  useEffect(() => {
    const t = setTimeout(fetchRows, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, product, region, state, fromDate, toDate, minValue, maxValue]);

  const clearFilters = () => {
    setQ(''); setProduct(''); setRegion(''); setState('');
    setFromDate(''); setToDate(''); setMinValue(''); setMaxValue('');
  };
  const hasFilter = q || product || region || state || fromDate || toDate || minValue || maxValue;

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const downloadZip = async () => {
    if (selected.size === 0) return;
    setZipping(true); setError('');
    try {
      const res = await axios.post(
        `${API}/performance-archive/zip`,
        { ids: [...selected] },
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `performance-archive-${Date.now()}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'ZIP failed');
    } finally { setZipping(false); }
  };

  const runBackfill = async () => {
    if (!await confirm({ message: 'Materialise Performance Archive rows for ALL historical won/completed leads? Safe to run multiple times.' })) return;
    setBackfilling(true); setError('');
    try {
      const res = await axios.post(`${API}/performance-archive/backfill`);
      notify(`Backfill: scanned ${res.data.scanned}, created/updated ${res.data.created}, skipped ${res.data.skipped}, failed ${res.data.failed}`);
      fetchRows();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBackfilling(false); }
  };

  const totalValue = useMemo(() => rows.reduce((s, r) => s + (r.order_value || 0), 0), [rows]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="performance-archive-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-[#00a69c] font-black text-2xl" style={{ fontFamily: 'Outfit' }}>
            <Archive weight="fill" className="w-7 h-7" />
            Performance Archive
          </div>
          <p className="text-sm text-[#3B5A54] mt-1 max-w-2xl">
            Auto-generated Customer Satisfaction Reports + uploaded Purchase Orders from completed leads. Pull these for tender performance-criteria clauses.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setSuggestOpen(true)}
            className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
            data-testid="perf-suggest-open-btn">
            <Sparkle weight="fill" className="inline w-3.5 h-3.5 -mt-0.5 mr-1" />
            Suggest evidence for tender
          </button>
          {canBackfill && (
            <button type="button" onClick={runBackfill} disabled={backfilling}
              className="text-xs px-3 py-1.5 rounded border border-[#D5E8E4] text-[#3B5A54] hover:bg-gray-50 disabled:opacity-50"
              data-testid="perf-backfill-btn">
              <ArrowClockwise className="inline w-3.5 h-3.5 -mt-0.5 mr-1" />
              {backfilling ? 'Backfilling…' : 'Backfill historical'}
            </button>
          )}
        </div>
      </div>

      {/* Counter strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Total records" value={total} testid="perf-total" />
        <Stat label="Matching filters" value={matched} testid="perf-matched" />
        <Stat label="Selected" value={selected.size} testid="perf-selected" />
        <Stat label="Total order value" value={inr(totalValue)} small testid="perf-value" />
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-[#D5E8E4] p-4 mb-4 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2 relative">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">Search</label>
            <MagnifyingGlass className="absolute left-2 bottom-2 w-4 h-4 text-[#6D8E87]" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Customer, PO #, product, sales owner, city…"
              className="w-full text-sm border border-[#D5E8E4] rounded pl-7 pr-2 py-1.5 bg-white"
              data-testid="perf-filter-q" />
          </div>
          <Field label="Product" value={product} onChange={setProduct} testid="perf-filter-product" />
          <Field label="Region" value={region} onChange={setRegion} testid="perf-filter-region" />
          <Field label="State" value={state} onChange={setState} testid="perf-filter-state" />
          <Field label="From date" type="date" value={fromDate} onChange={setFromDate} testid="perf-filter-from" />
          <Field label="To date" type="date" value={toDate} onChange={setToDate} testid="perf-filter-to" />
          <Field label="Min value (₹)" type="number" value={minValue} onChange={setMinValue} testid="perf-filter-min" />
          <Field label="Max value (₹)" type="number" value={maxValue} onChange={setMaxValue} testid="perf-filter-max" />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button type="button" onClick={clearFilters} disabled={!hasFilter}
            className="text-xs px-3 py-1.5 rounded border border-[#D5E8E4] text-[#3B5A54] hover:bg-gray-50 disabled:opacity-40"
            data-testid="perf-clear-filters">Clear filters</button>
          <button type="button" onClick={downloadZip} disabled={selected.size === 0 || zipping}
            className="text-xs px-3 py-1.5 rounded bg-[#00a69c] text-white font-semibold hover:bg-[#00bfb3] disabled:opacity-50"
            data-testid="perf-download-zip">
            <FileArrowDown className="inline w-3.5 h-3.5 -mt-0.5 mr-1" />
            {zipping ? 'Zipping…' : `Download ${selected.size || ''} as ZIP`}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded border border-rose-200 bg-rose-50 text-rose-800 text-xs" data-testid="perf-error">{String(error)}</div>
      )}

      {/* Results table */}
      <div className="bg-white rounded-xl border border-[#D5E8E4] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4FAF8] text-[11px] uppercase tracking-wide text-[#6D8E87]">
              <tr>
                <th className="px-3 py-2 w-8">
                  <button type="button" onClick={toggleAll} data-testid="perf-select-all">
                    {selected.size > 0 && selected.size === rows.length
                      ? <CheckSquare weight="fill" className="w-4 h-4 text-[#00a69c]" />
                      : <Square className="w-4 h-4 text-[#6D8E87]" />}
                  </button>
                </th>
                <th className="text-left px-3 py-2 font-semibold">Customer</th>
                <th className="text-left px-3 py-2 font-semibold">Products</th>
                <th className="text-left px-3 py-2 font-semibold">Region</th>
                <th className="text-left px-3 py-2 font-semibold">PO #</th>
                <th className="text-right px-3 py-2 font-semibold">Value</th>
                <th className="text-left px-3 py-2 font-semibold">Completed</th>
                <th className="text-left px-3 py-2 font-semibold">Sales Owner</th>
                <th className="text-left px-3 py-2 font-semibold">Files</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <TableRowsSkeleton cols={9} />
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-10 text-center text-[#6D8E87] text-xs" data-testid="perf-empty">
                    {hasFilter ? 'No records match the current filters.' : 'No performance records yet. Run backfill (President) or complete a lead to seed the archive.'}
                  </td>
                </tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-t border-[#EEF6F3] hover:bg-[#FAFDFC]" data-testid={`perf-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => toggleOne(r.id)} data-testid={`perf-toggle-${r.id}`}>
                      {selected.has(r.id)
                        ? <CheckSquare weight="fill" className="w-4 h-4 text-[#00a69c]" />
                        : <Square className="w-4 h-4 text-[#6D8E87]" />}
                    </button>
                  </td>
                  <td className="px-3 py-2 max-w-[220px]">
                    <div className="font-semibold text-[#052A23] line-clamp-2">{r.customer_name || '—'}</div>
                    <div className="text-[10px] text-[#6D8E87]">{r.city}{r.state ? `, ${r.state}` : ''}</div>
                  </td>
                  <td className="px-3 py-2 max-w-[220px]">
                    <div className="line-clamp-2 text-[#3B5A54]">
                      {(r.product_names || []).join(', ') || <span className="italic text-[#8FA9A3]">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[#3B5A54]">{r.region || '—'}</td>
                  <td className="px-3 py-2 text-[#052A23] font-semibold whitespace-nowrap">{r.po_number || '—'}</td>
                  <td className="px-3 py-2 text-right text-[#052A23] font-semibold whitespace-nowrap">{inr(r.order_value)}</td>
                  <td className="px-3 py-2 text-[#3B5A54] whitespace-nowrap">{fmtDate(r.completed_at || r.won_at)}</td>
                  <td className="px-3 py-2 text-[#3B5A54]">{r.sales_owner_name || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex gap-1">
                      {r.po_file_url ? (
                        <a href={authedUrl(r.po_file_url)} target="_blank" rel="noreferrer"
                          className="text-[11px] px-2 py-1 rounded border border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
                          data-testid={`perf-po-${r.id}`}>PO</a>
                      ) : <span className="text-[10px] text-[#8FA9A3]">no PO</span>}
                      {r.csr_file_url ? (
                        <a href={authedUrl(r.csr_file_url)} target="_blank" rel="noreferrer"
                          className="text-[11px] px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                          data-testid={`perf-csr-${r.id}`}>CSR</a>
                      ) : <span className="text-[10px] text-[#8FA9A3]">no CSR</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* iter109 — Suggest-for-tender dialog */}
      {suggestOpen && <SuggestDialog onClose={() => setSuggestOpen(false)} />}
    </div>
  );
}

function SuggestDialog({ onClose }) {
  const [region, setRegion] = useState('');
  const [state, setStateIn] = useState('');
  const [productScope, setProductScope] = useState('');
  const [limit, setLimit] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    setLoading(true); setError(''); setResult(null); setCopied(false);
    try {
      const res = await axios.post(`${API}/performance-archive/suggest`, {
        region, state, product_scope: productScope, limit: Number(limit) || 3,
      });
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const copySnippet = async () => {
    if (!result?.snippet) return;
    try { await navigator.clipboard.writeText(result.snippet); }
    catch {
      const ta = document.createElement('textarea');
      ta.value = result.snippet;
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="perf-suggest-dialog">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#E2ECEA] flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-indigo-800 font-bold text-lg">
              <Sparkle weight="fill" className="w-5 h-5" />
              Suggest performance evidence
            </div>
            <p className="text-xs text-[#6D8E87] mt-1">
              Enter the tender's product scope + target region. We'll rank your archived orders and give you a ready-to-paste snippet.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-[#6D8E87]" data-testid="perf-suggest-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">Product scope from tender</label>
            <textarea rows={2} value={productScope} onChange={(e) => setProductScope(e.target.value)}
              placeholder="e.g. Hospital furniture (beds, patient chairs, trolleys) for AIIMS-class facilities"
              className="w-full text-sm border border-[#D5E8E4] rounded px-2 py-1.5 bg-white"
              data-testid="perf-suggest-product" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">Region</label>
            <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="South / North / East / West"
              className="w-full text-sm border border-[#D5E8E4] rounded px-2 py-1.5 bg-white"
              data-testid="perf-suggest-region" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">State</label>
            <input value={state} onChange={(e) => setStateIn(e.target.value)} placeholder="Andhra Pradesh"
              className="w-full text-sm border border-[#D5E8E4] rounded px-2 py-1.5 bg-white"
              data-testid="perf-suggest-state" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">Top N</label>
            <input type="number" min={1} max={20} value={limit} onChange={(e) => setLimit(e.target.value)}
              className="w-full text-sm border border-[#D5E8E4] rounded px-2 py-1.5 bg-white"
              data-testid="perf-suggest-limit" />
          </div>
        </div>

        <div className="px-5 pb-3 flex items-center gap-2">
          <button type="button" onClick={run} disabled={loading}
            className="text-sm px-4 py-1.5 rounded bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50"
            data-testid="perf-suggest-run">
            <Sparkle weight="fill" className="inline w-4 h-4 -mt-0.5 mr-1" />
            {loading ? 'Ranking…' : 'Suggest evidence'}
          </button>
        </div>

        {error && (
          <div className="mx-5 mb-3 px-3 py-2 rounded border border-rose-200 bg-rose-50 text-rose-800 text-xs" data-testid="perf-suggest-error">{error}</div>
        )}

        {result && (
          <div className="px-5 pb-5" data-testid="perf-suggest-result">
            <div className="text-[11px] uppercase tracking-wide text-[#6D8E87] mb-1">Ready-to-paste snippet</div>
            <textarea readOnly value={result.snippet}
              rows={Math.max(3, result.rows?.length || 3)}
              className="w-full text-xs font-mono border border-indigo-200 rounded px-3 py-2 bg-indigo-50/40"
              data-testid="perf-suggest-snippet" />
            <div className="mt-2 flex items-center gap-2">
              <button type="button" onClick={copySnippet}
                className={`text-xs px-3 py-1.5 rounded font-semibold ${copied ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                data-testid="perf-suggest-copy">
                <ClipboardText className="inline w-3.5 h-3.5 -mt-0.5 mr-1" />
                {copied ? 'Copied!' : 'Copy snippet'}
              </button>
              <span className="text-[11px] text-[#6D8E87]">{result.rows?.length || 0} evidence row(s) ranked by relevance</span>
            </div>
            {result.rows?.length > 0 && (
              <div className="mt-3 border border-[#D5E8E4] rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#F4FAF8] text-[10px] uppercase tracking-wide text-[#6D8E87]">
                    <tr>
                      <th className="text-left px-2 py-1.5">#</th>
                      <th className="text-left px-2 py-1.5">Customer</th>
                      <th className="text-left px-2 py-1.5">Products</th>
                      <th className="text-left px-2 py-1.5">State</th>
                      <th className="text-right px-2 py-1.5">Value</th>
                      <th className="text-left px-2 py-1.5">PO #</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r, i) => (
                      <tr key={r.id} className="border-t border-[#EEF6F3]" data-testid={`perf-suggest-row-${i}`}>
                        <td className="px-2 py-1.5 text-[#052A23] font-semibold">{i + 1}</td>
                        <td className="px-2 py-1.5 text-[#052A23]">{r.customer_name}</td>
                        <td className="px-2 py-1.5 text-[#3B5A54] max-w-[180px]"><div className="truncate">{(r.product_names || []).join(', ')}</div></td>
                        <td className="px-2 py-1.5 text-[#3B5A54]">{r.state || '—'}</td>
                        <td className="px-2 py-1.5 text-right text-[#052A23] font-semibold whitespace-nowrap">{inr(r.order_value)}</td>
                        <td className="px-2 py-1.5 text-[#3B5A54]">{r.po_number || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, small, testid }) {
  return (
    <div className="bg-white rounded-lg border border-[#D5E8E4] px-4 py-3" data-testid={testid}>
      <div className={`${small ? 'text-lg' : 'text-2xl'} font-black text-[#052A23]`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-[#6D8E87]">{label}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] block mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-[#D5E8E4] rounded px-2 py-1.5 bg-white"
        data-testid={testid} />
    </div>
  );
}
